<template>
  <div class="modal-column">
    <div class="padding flex-1">
      <!-- 上传Excel并直接显示字段映射 -->
      <div>
        <p class="mb-10px">第一步：上传您的Excel文件</p>
        <span class="blue-color text-hover" @click="$refs.file.click()">选取文件</span>
        <div class="h-30px mt-8px"><span v-if="fileName">已选：{{ fileName }}</span></div>
        <input type="file" style="visibility: hidden;position: absolute" @change="fileChange()" ref="file">
        
        <!-- 字段映射区域 -->
        <div v-if="excelHeaders.length > 0" class="mt-20px">
          <p class="mb-10px">第二步：请将系统字段与Excel列进行关联（带*为必填项）</p>
          <div class="mapping-container">
            <div class="mapping-header">
              <span class="system-field">日记账列表字段</span>
              <span class="excel-field">用户上传列表字段</span>
            </div>
            
            <div class="mapping-row" v-for="item in fieldMappings" :key="item.systemField">
              <div class="system-field-cell">
                <span v-if="item.required" class="required-star">*</span>
                <span>{{ item.label }}</span>
              </div>
              <div class="excel-field-cell">
                <Select 
                  v-model="item.excelColumn" 
                  :datas="excelHeaderOptions" 
                  filterable
                  :placeholder="item.required ? '请选择' : '可选'"
                  :deletable="!item.required"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="modal-column-btn">
      <Button @click="$emit('close')" :loading="loading">取消</Button>
      <Button color="green" @click="importData" :disabled="!fileName || excelHeaders.length === 0" :loading="loading">{{ account.name }}账单导入</Button>
    </div>
  </div>
</template>

<script>
/**
 * 灵活的Excel导入组件 - 支持字段映射
 */
import { modal } from "heyui.ext";
import CashierJournal from "@/api/CashierJournal";
import * as XLSX from 'xlsx'

export default {
  name: "CashierImportMapping",
  props: {
    account: Object,
    ioType: String
  },
  data() {
    return {
      file: null,
      loading: false,
      excelHeaders: [],
      rawExcelData: [],
      headerRowIndex: 0,
      
      // 系统字段定义
      systemFields: [
        { systemField: 'billDate', label: '日期', required: true, excelColumn: '' },
        { systemField: 'digest', label: '摘要', required: true, excelColumn: '' },
        { systemField: 'debit', label: '收入', required: true, excelColumn: '' },
        { systemField: 'credit', label: '支出', required: true, excelColumn: '' },
        { systemField: 'remark', label: '备注', required: false, excelColumn: '' },
        { systemField: 'billNo', label: '票据号', required: false, excelColumn: '' }
      ],
      
      fieldMappings: []
    }
  },
  computed: {
    fileName() {
      if (this.file) {
        return this.file.name;
      }
      return '';
    },
    
    excelHeaderOptions() {
      return this.excelHeaders.map(header => ({
        key: header,
        title: header
      }));
    }
  },
  methods: {
    // 文件选择
    fileChange() {
      this.file = this.$refs.file.files[0];
      if (this.file) {
        this.parseExcel();
      }
    },
    
    parseExcel() {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          const range = XLSX.utils.decode_range(worksheet['!ref']);
          const merges = worksheet['!merges'] || [];
          
          let headerRowIndex = 0;
          for (let r = range.s.r; r <= range.e.r; r++) {
            let nonEmptyCount = 0;
            const rowHeaders = [];
            for (let col = range.s.c; col <= range.e.c; col++) {
              const cellAddress = XLSX.utils.encode_cell({ r, c: col });
              const cell = worksheet[cellAddress];
              if (cell && cell.v !== undefined && cell.v !== null && String(cell.v).trim() !== '') {
                nonEmptyCount++;
                rowHeaders.push(String(cell.v).trim());
              }
            }
            const isMergedRow = merges.some(m => m.s.r <= r && r <= m.e.r && (m.e.c - m.s.c > 0 || m.e.r - m.s.r > 0));
            if (!isMergedRow && nonEmptyCount >= 3) {
              headerRowIndex = r;
              this.excelHeaders = rowHeaders;
              break;
            }
          }
          
          if (this.excelHeaders.length < 3) {
            this.$Message.error('未找到有效的表头行（至少需要3个字段）');
            this.excelHeaders = [];
            return;
          }
          
          this.headerRowIndex = headerRowIndex;
          this.rawExcelData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          this.autoMatchFields();
          
          this.$Message.success('Excel文件解析成功，表头位于第' + (headerRowIndex + 1) + '行');
        } catch (error) {
          console.error(error);
          this.$Message.error('Excel文件解析失败，请检查文件格式');
          this.excelHeaders = [];
        }
      };
      reader.readAsArrayBuffer(this.file);
    },
    
    // 自动匹配字段
    autoMatchFields() {
      // 复制系统字段配置
      this.fieldMappings = JSON.parse(JSON.stringify(this.systemFields));
      
      // 定义常见的字段名称映射
      const fieldAliases = {
        'billDate': ['日期', '时间', '发生日期', '交易日期', 'date'],
        'digest': ['摘要', '说明', '描述', '内容', 'summary'],
        'debit': ['收入', '借方', '进账', '收款', '收入金额', 'debit', 'income'],
        'credit': ['支出', '贷方', '出账', '付款', '支出金额', 'credit', 'expense'],
        'remark': ['备注', '注释', '说明', 'remark', 'note'],
        'billNo': ['票据号', '单据号', '凭证号', '发票号']
      };
      
      // 遍历每个系统字段，尝试自动匹配
      this.fieldMappings.forEach(mapping => {
        const aliases = fieldAliases[mapping.systemField];
        if (!aliases) return;
        
        // 查找匹配的Excel列
        for (const alias of aliases) {
          const matchedColumn = this.excelHeaders.find(header => 
            header.toLowerCase().includes(alias.toLowerCase()) ||
            alias.toLowerCase().includes(header.toLowerCase())
          );
          
          if (matchedColumn) {
            mapping.excelColumn = matchedColumn;
            break;
          }
        }
      });
    },
    

    
    // 执行导入
    importData() {
      // 验证必填字段是否已映射
      const requiredFields = this.fieldMappings.filter(f => f.required);
      const unmappedRequired = requiredFields.filter(f => !f.excelColumn);
      
      if (unmappedRequired.length > 0) {
        const fieldNames = unmappedRequired.map(f => f.label).join('、');
        this.$Message.error(`以下必填字段未映射：${fieldNames}`);
        return;
      }
      
      // 构建字段映射关系
      const fieldMap = {};
      this.fieldMappings.forEach(mapping => {
        if (mapping.excelColumn) {
          fieldMap[mapping.systemField] = mapping.excelColumn;
        }
      });
      
      // 准备FormData
      const formData = new FormData();
      formData.append('file', this.file);
      formData.append('accountId', this.account.id);
      formData.append('type', this.ioType);
      formData.append('fieldMap', JSON.stringify(fieldMap));
      formData.append('headerRowIndex', this.headerRowIndex);
      
      this.loading = true;
      
      CashierJournal.importWithMapping(formData).then(({ data }) => {
        if (data && data.length) {
          modal({
            title: '如下记录发生错误未导入成功',
            buttons: [{
              type: 'cancel',
              name: '确认',
              style: 'primary'
            }],
            content: data.join("<br>"),
            events: {
              cancel: modal => {
                this.$refs.file.value = "";
                this.file = null;
                this.excelHeaders = [];
                this.$emit('success', data);
                modal.close();
              }
            }
          });
        } else {
          this.$Message("亲,导入成功~");
          this.$refs.file.value = "";
          this.file = null;
          this.excelHeaders = [];
          this.$emit('success', data);
        }
      }).finally(() => {
        this.loading = false;
      }).catch(() => {
        this.$refs.file.value = "";
        this.file = null;
      });
    }
  }
}
</script>

<style scoped>

.mapping-container {
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  padding: 16px;
  background: #fafafa;
}

.mapping-header {
  display: flex;
  font-weight: bold;
  padding-bottom: 12px;
  border-bottom: 2px solid #d9d9d9;
  margin-bottom: 12px;
}

.system-field {
  flex: 1;
  color: #333;
}

.excel-field {
  flex: 1.5;
  color: #333;
}

.mapping-row {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e8e8e8;
}

.mapping-row:last-child {
  border-bottom: none;
}

.system-field-cell {
  flex: 1;
  padding-right: 12px;
}

.excel-field-cell {
  flex: 1.5;
}

.required-star {
  color: #ff4d4f;
  margin-right: 4px;
}

</style>